import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import { Check, ChevronDown, Zap } from 'lucide-react'
import api from 'api/api'
import queryClient from 'api/queryClient'
import { SATOSHI_UNIT, USD_UNIT } from 'lib/bsv-util'
import useStore from 'state/knovStore'
import useStreamFilter, { Filter, withDepth } from 'components/filters/useStreamFilters'
import WithTooltip from 'components/shared/WithTooltip'
import {
    getStreamOrderQueryKey,
    shouldInvalidateStreamOrderQuery,
} from 'components/panels/streamOrderMapping'
import {
    ORDINALS_MIN_SATS_STOPS,
    ORDINALS_MIN_SATS_STORAGE_KEY,
    formatOrdinalsMinSats,
    formatOrdinalsMinSatsAmount,
    ordinalsStreamQueryFilter,
    resolveOrdinalsMinSats,
} from 'components/panels/ordinalsThreshold'
import styles from 'components/panels/stream-order.module.scss'

// Remember the dial: space options for logged-in viewers (merged server-side),
// localStorage for logged-out ones.
function persistOrdinalsMinSats(value: number) {
    const currentUser = window.gon?.currentUser
    if (currentUser) {
        api.updateUserSpaceOptions({ ordinalsMinSats: value })
        // Keep the local snapshot fresh — resolveOrdinalsMinSats reads gon.
        currentUser.space_options = {
            ...(currentUser.space_options || {}),
            ordinalsMinSats: value,
        }
    } else {
        try {
            window.localStorage?.setItem(ORDINALS_MIN_SATS_STORAGE_KEY, String(value))
        } catch {
            // Storage unavailable; the dial still applies for this session.
        }
    }
}

type ApplyOrdinalsMinSatsArgs = {
    filter: Filter
    setStreamFilter: (filter: Filter) => void
    value: number
    fromLocation: string
}

// Shared by the pill and the empty state's "Show all" escape hatch.
export function applyOrdinalsMinSats({
    filter,
    setStreamFilter,
    value,
    fromLocation,
}: ApplyOrdinalsMinSatsArgs) {
    window.logEv('CLICK_ORDINALS_MIN_SATS', {
        min_sats: value,
        previous_min_sats: resolveOrdinalsMinSats(filter),
        from_location: fromLocation,
    })
    persistOrdinalsMinSats(value)

    const newFilter = { ...filter, min_sats: value }
    // Mirror StreamOrder: mark an expired cached destination stale before
    // activating it so the swap can't serve an outdated page.
    const queryKey = getStreamOrderQueryKey(ordinalsStreamQueryFilter(withDepth(newFilter)))
    if (shouldInvalidateStreamOrderQuery(queryClient.getQueryState(queryKey))) {
        queryClient.invalidateQueries({ queryKey, exact: true })
    }
    setStreamFilter(newFilter)
}

function stopTip(stop: number, useUsd: boolean, exchangeRate: number): string {
    if (!stop) return 'Every eligible ordinal'
    return `Only ordinals with at least ${formatOrdinalsMinSatsAmount(stop, useUsd, {
        exchangeRate,
    })} of upvalue`
}

// The quality dial for the ordinals public wall: a stepped minimum-upvalue
// stop, rendered as a pill + portal menu in the Top pill's visual language.
export default function OrdinalsMinSatsPill({
    filter,
    panelId,
}: {
    filter: Filter
    panelId: string
}) {
    const { setStreamFilter } = useStreamFilter(panelId)
    // The dial's tint tracks the currency the viewer reads amounts in.
    const bsvUnit = useStore(state => state.bsvUnit)
    const useSats = bsvUnit === SATOSHI_UNIT
    const useUsd = bsvUnit === USD_UNIT
    const exchangeRate = useStore(state => state.exchangeRate)
    const showUsd = useUsd && exchangeRate > 0
    // A '$0.01+' row already names its unit; a '10K+' row doesn't.
    const unitSuffix = showUsd ? '' : ' sats'
    const [open, setOpen] = useState(false)
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>()
    const pillRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    const current = resolveOrdinalsMinSats(filter)

    useEffect(() => {
        if (!open) return
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                pillRef.current &&
                !pillRef.current.contains(event.target as Node)
            ) {
                setMenuStyle(undefined)
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [open])

    const toggleMenu = () => {
        if (open) {
            setMenuStyle(undefined)
            setOpen(false)
            return
        }
        const rect = pillRef.current?.getBoundingClientRect()
        if (!rect) return

        setMenuStyle({
            position: 'fixed',
            top: rect.bottom + 6,
            left: Math.max(8, Math.min(rect.left, window.innerWidth - 180)),
            zIndex: 1000,
        })
        setOpen(true)
    }

    const select = (value: number) => {
        setMenuStyle(undefined)
        setOpen(false)
        if (value === current) return
        applyOrdinalsMinSats({
            filter,
            setStreamFilter,
            value,
            fromLocation: 'ordinals_min_sats_pill',
        })
    }

    return (
        <>
            <div ref={pillRef} className={styles.topPillAnchor}>
                <WithTooltip asChild tip="Minimum upvalue to show" disable={open}>
                    <button
                        type="button"
                        className={cn(
                            styles.orderBtn,
                            styles.minSatsBtn,
                            useSats && styles.minSatsBtnSats,
                            useUsd && styles.minSatsBtnUsd,
                        )}
                        data-testid="ordinals-min-sats-pill"
                        aria-label="Minimum upvalue"
                        aria-haspopup="menu"
                        aria-expanded={open}
                        onClick={ev => {
                            ev.stopPropagation()
                            ev.preventDefault()
                            toggleMenu()
                        }}
                    >
                        <Zap size={13} className={styles.minSatsZap} />
                        <span className={styles.orderLabel}>
                            {formatOrdinalsMinSats(current, showUsd, exchangeRate)}
                        </span>
                        <ChevronDown size={12} className={styles.topChevron} />
                    </button>
                </WithTooltip>
            </div>
            {open &&
                menuStyle &&
                ReactDOM.createPortal(
                    <div ref={menuRef} className={styles.topMenu} style={menuStyle} role="menu">
                        {ORDINALS_MIN_SATS_STOPS.map(stop => {
                            const active = stop === current
                            return (
                                <button
                                    key={stop}
                                    type="button"
                                    role="menuitem"
                                    title={stopTip(stop, showUsd, exchangeRate)}
                                    className={cn(
                                        styles.topMenuItem,
                                        active && styles.topMenuItemActive,
                                    )}
                                    onClick={ev => {
                                        ev.stopPropagation()
                                        ev.preventDefault()
                                        select(stop)
                                    }}
                                >
                                    <span>
                                        {stop === 0
                                            ? 'All'
                                            : `${formatOrdinalsMinSats(
                                                  stop,
                                                  showUsd,
                                                  exchangeRate,
                                              )}${unitSuffix}`}
                                    </span>
                                    {active && <Check size={14} className={styles.topMenuCheck} />}
                                </button>
                            )
                        })}
                    </div>,
                    document.body,
                )}
        </>
    )
}
